import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  useMetricsUi,
  SectionHead,
  headCellSx,
  textCellSx,
  MONO_FONT,
} from "../../components/metrics/metricsUi";
import { MetricLabel } from "./metricDefinitions";

// Demand types as the forecast service names them → plain words.
export const DEMAND_TYPE_LABEL = {
  stable: "Steady",
  trending: "Growing / declining",
  seasonal: "Seasonal",
  intermittent: "Occasional",
  lumpy: "Occasional and irregular",
  new_product: "New product",
  no_demand: "No sales",
};

// "This SKU …" — what each demand type means for a planner.
const DEMAND_TYPE_SENTENCE = {
  stable: "sells at a fairly steady rate",
  trending: "has been growing or declining over time",
  seasonal: "follows a pattern that repeats every year",
  intermittent: "sells only now and then, but in similar amounts when it does",
  lumpy: "sells only now and then, and in very different amounts",
  new_product: "has only a short sales history so far",
  no_demand: "has had no recent sales",
};

// What each method does, in one plain sentence.
const METHOD_PLAIN = {
  seasonal_naive: "repeats what sold at the same point last time round",
  naive: "repeats the most recent period's sales",
  historic_average: "uses the average of all past sales",
  ses: "follows the current level of sales, giving the latest periods the most weight",
  holt: "follows the current level of sales and the direction they are heading",
  holt_winters: "follows the level of sales, the direction they are heading and the yearly pattern",
  tbats: "follows the level of sales, the direction they are heading and the yearly pattern, and steadies very uneven sales before learning from them",
  auto_ets: "smooths past sales, finding the best mix of level, direction and seasonal pattern",
  auto_arima: "learns how each period's sales relate to the periods before",
  prophet: "learns the long-term direction of sales and the pattern that repeats through the year",
  prophet_x: "learns the direction and yearly pattern of sales, plus the effect of holidays, promotions and discounts",
  sarimax: "learns how each period's sales relate to the periods before, and also takes holidays, promotions and discounts into account",
  croston: "is made for products that sell now and then: it estimates how often a sale happens and its usual size",
  croston_sba: "is made for products that sell now and then: it estimates how often a sale happens and its usual size, without overshooting",
  croston_optimized: "is made for products that sell now and then: it estimates how often a sale happens and its usual size",
  tsb: "is made for products that sell now and then: it tracks how likely a sale is each period, and adapts quickly if sales stop",
  imapa: "is made for products that sell now and then: it looks at the sales over several period lengths to see the pattern clearly",
  adida: "is made for products that sell now and then: it groups periods together to see the pattern clearly, then spreads it back out",
};

// The score that picked the winner, in words.
const METRIC_PLAIN = {
  wape: "each period's forecast",
  wape_total: "the total for the forecast period",
  mape: "each period's forecast",
  smape: "each period's forecast",
};

// Height of the "all methods tested" panels; up to 17 methods are listed, so
// they scroll inside this instead of pushing the page down.
const DETAILS_MAX_HEIGHT = 360;

const PERIOD_UNIT = { D: "day", W: "week", M: "month" };
// The "same as last time" benchmark repeats one seasonal cycle back:
// a week for daily sales, a year for weekly or monthly sales.
const BENCHMARK_PHRASE = {
  day: "simply repeating what sold on the same day last week",
  week: "simply repeating what sold in the same week last year",
  month: "simply repeating what sold in the same month last year",
};
const times = (n) => (Number(n) === 1 ? "once" : Number(n) === 2 ? "twice" : `${n} times over`);

// Why a method wasn't scored, in plain words (the service's text is technical).
const plainError = (error) =>
  /^needs \d+ periods/.test(error) || /not enough history/i.test(error)
    ? "Not enough sales history yet to test it fairly."
    : "Could not be fitted to this sales history.";

const isNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
const pct = (v) => (isNum(v) ? `${Number(v).toFixed(1)}%` : "—");
const signedPct = (v) => (isNum(v) ? `${Number(v) > 0 ? "+" : ""}${Number(v).toFixed(1)}%` : "—");
const round = (v) => Math.round(Number(v));
const plural = (n, unit) => `${n} ${unit}${Number(n) === 1 ? "" : "s"}`;

/**
 * Plain-language reasons for the chosen method, built from the facts the
 * service stored (not its technical reason text).
 * Returns [{ title, text }].
 */
export function plainReasons(model) {
  if (!model) return [];
  const unit = PERIOD_UNIT[String(model.frequency || "").charAt(0).toUpperCase()] || "period";
  const historyMatch = (model.reasons || [])
    .map((r) => /History:\s*(\d+)/i.exec(r))
    .find(Boolean);
  const historyLen = historyMatch ? Number(historyMatch[1]) : null;
  const label = model.model_label || model.model;
  const metric = model.metric || "wape";
  const scoreOf = (c) => (c ? c[metric] ?? c.wape : null);
  const chosenScore = model.tested_error?.[metric] ?? model.tested_error?.wape;
  const scored = (model.candidates || []).filter((c) => !c.error && isNum(scoreOf(c)));
  const tested = scored.length;
  const runnerUp = scored
    .filter((c) => c.model !== model.model)
    .sort((a, b) => scoreOf(a) - scoreOf(b))[0];
  const isPct = metric !== "mase";
  const out = [];

  const demand = DEMAND_TYPE_SENTENCE[model.demand_type];
  // Sales fill: the service counts the estimated periods as history; say them apart.
  const fill = model.sales_fill?.applied ? model.sales_fill : null;
  const realLen = historyLen != null && fill ? Math.max(0, historyLen - (Number(fill.periods) || 0)) : historyLen;
  if (demand) {
    out.push({
      title: "This SKU's sales",
      text:
        `It ${demand}` +
        (fill && !realLen
          ? ` (no sales of its own yet — all ${plural(fill.periods, unit)} were estimated by Sales Fill)`
          : realLen
            ? fill
              ? ` (${plural(realLen, unit)} of sales history, plus ${plural(fill.periods, unit)} estimated before the first sale by Sales Fill)`
              : ` (${plural(realLen, unit)} of sales history)`
            : "") +
        ".",
    });
  }

  if (tested) {
    const h = model.backtest_horizon;
    const w = model.backtest_windows;
    out.push({
      title: "How we chose",
      text:
        `We tried ${tested} forecasting method${tested === 1 ? "" : "s"}. ` +
        (h && w
          ? `Each one had to forecast ${plural(h, unit)} of past sales it had not seen — ${times(w)} — and we compared its guesses with what actually sold.`
          : "Each one forecast past sales it had not seen, and we compared its guesses with what actually sold."),
    });
  }

  const what = METHOD_PLAIN[model.model];
  out.push({
    title: `Why ${label}`,
    text:
      (isNum(chosenScore) && isPct
        ? `It came closest: on ${METRIC_PLAIN[metric] || "the forecast"} it was off by about ${round(chosenScore)}% — roughly ${round(chosenScore)} units for every 100 sold.`
        : "It came closest to what actually sold.") + (what ? ` This method ${what}.` : ""),
  });

  const comparisons = [];
  if (model.model === model.benchmark) {
    comparisons.push(`No method did clearly better than ${BENCHMARK_PHRASE[unit] || "simply repeating past sales from the same point last time round"}, so that is what we used.`);
  } else if (isNum(model.beat_benchmark_pct) && Number(model.beat_benchmark_pct) > 0) {
    comparisons.push(`It was ${round(model.beat_benchmark_pct)}% more accurate than ${BENCHMARK_PHRASE[unit] || "simply repeating past sales from the same point last time round"}.`);
  }
  if (runnerUp && isNum(scoreOf(runnerUp)) && isPct) {
    comparisons.push(`The next best, ${runnerUp.label || runnerUp.model}, was off by about ${round(scoreOf(runnerUp))}%.`);
  }
  if (comparisons.length) out.push({ title: "Compared with the others", text: comparisons.join(" ") });

  // Trust: per-unit error, the same guide as the Updated Forecast guide.
  // When the winner was picked on the period total, the total can be close
  // while single periods swing a lot — say both rather than contradict.
  const perUnit = model.tested_error?.wape;
  if (isNum(perUnit)) {
    const e = Number(perUnit);
    const totalClose = metric === "wape_total" && isNum(chosenScore) && Number(chosenScore) <= 30;
    out.push({
      title: "How far to trust it",
      text:
        e < 25
          ? "This SKU's sales are fairly predictable, so the forecast is a reliable guide."
          : totalClose && e > 50
            ? `The total for the period is a fair guide for ordering, but single ${unit}s swing a lot (about ${round(e)}% off on average), so don't rely on any one ${unit}'s number.`
            : e <= 50
              ? `Use it as a good guide, but expect some ${unit}s to come in higher or lower.`
              : "This SKU's sales are hard to predict, so treat the forecast as a rough guide and check the likely range.",
    });
  }
  return out;
}

/**
 * How a demand-forecast-engine run picked its model: a plain explanation
 * first, with the service's technical reasons and every method's score
 * behind a toggle. `model` is the stored forecast-data `forecast_model`.
 */
export default function ForecastModelSection({ model, defaultDetailsOpen = false }) {
  const ui = useMetricsUi();
  const { colors } = ui;
  // Opened from "Why this forecast method?": show every method tested straight away.
  const [detailsOpen, setDetailsOpen] = useState(defaultDetailsOpen);
  if (!model) return null;

  const candidates = [...(model.candidates || [])].sort((a, b) => {
    const rank = (c) =>
      c.model === model.model ? 0 : c.error ? 3 : c.role === "benchmark" ? 1 : 2;
    return rank(a) - rank(b) || (a.wape ?? Infinity) - (b.wape ?? Infinity);
  });
  const roleOf = (c) =>
    c.model === model.model
      ? { label: "Chosen", color: "success" }
      : c.error
        ? { label: "Left out", color: "default" }
        : c.role === "benchmark"
          ? { label: "Same as last year", color: "default" }
          : { label: "Tested", color: "default" };

  const subtitle = [model.model_label, DEMAND_TYPE_LABEL[model.demand_type] || model.demand_type]
    .filter(Boolean)
    .join(" · ");
  const reasons = plainReasons(model);

  return (
    <Box>
      <SectionHead title="Why this forecast method?" subtitle={subtitle} />
      <Box
        sx={{
          ...ui.card,
          p: "14px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          textAlign: "left",
        }}
      >
        {reasons.map((r) => (
          <Box key={r.title}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>{r.title}</Typography>
            <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: colors.textSecondary }}>{r.text}</Typography>
          </Box>
        ))}
        {model.event_uplift?.events?.length > 0 && (
          <Box
            data-testid="uplift-note"
            sx={{ bgcolor: ui.warningBg, borderRadius: "8px", px: 1.5, py: 1 }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: ui.warning }}>
              Event uplift added on top ({round(model.event_uplift.total_units)} units)
            </Typography>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: colors.textPrimary }}>
              {model.event_uplift.events
                .map((e) => `${e.name} ${e.lift_pct > 0 ? "+" : ""}${Math.round(Number(e.lift_pct) * 10) / 10}%`)
                .join(", ")}
              . The accuracy figures here are for the method's forecast before this uplift; the uplift itself
              hasn't been tested.
            </Typography>
          </Box>
        )}
        {(model.warnings || []).length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {model.warnings.map((w, i) => (
              <Chip key={i} size="small" color="warning" variant="outlined" label={w} sx={{ fontSize: 11, height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }} />
            ))}
          </Box>
        )}
        <Box>
          <Button
            size="small"
            onClick={() => setDetailsOpen((o) => !o)}
            endIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={detailsOpen}
            sx={{ textTransform: "none", fontSize: 12.5, px: 0.5 }}
          >
            {detailsOpen ? "Hide" : "Show"} all methods tested
          </Button>
        </Box>
      </Box>

      <Collapse in={detailsOpen} unmountOnExit>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, alignItems: "start", mt: 2 }}>
          <Box
            sx={{
              ...ui.card,
              p: "14px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              textAlign: "left",
              minWidth: 0,
              maxHeight: DETAILS_MAX_HEIGHT,
              overflowY: "auto",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Technical notes</Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75, listStylePosition: "outside" }}
            >
              {(model.reasons || []).map((r, i) => (
                <Typography
                  component="li"
                  key={i}
                  sx={{ fontSize: 12.5, lineHeight: 1.55, color: colors.textSecondary, pl: 0.25, overflowWrap: "anywhere" }}
                >
                  {r}
                </Typography>
              ))}
            </Box>
            {(model.regressors || []).length > 0 && (
              <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                Signals offered: {model.regressors.join(", ").replace(/_/g, " ")}
              </Typography>
            )}
          </Box>

          <TableContainer sx={{ ...ui.card, p: 0, maxHeight: DETAILS_MAX_HEIGHT, overflowY: "auto" }}>
            {/* Header stays visible while the methods scroll. */}
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx(ui)}>Method</TableCell>
                  <TableCell sx={headCellSx(ui)}><MetricLabel metric="result">Result</MetricLabel></TableCell>
                  <TableCell sx={headCellSx(ui)} align="right"><MetricLabel metric="error_per_unit">Error per unit sold</MetricLabel></TableCell>
                  <TableCell sx={headCellSx(ui)} align="right"><MetricLabel metric="error_on_total">Error on total</MetricLabel></TableCell>
                  <TableCell sx={headCellSx(ui)} align="right"><MetricLabel metric="bias">Bias</MetricLabel></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((c) => {
                  const role = roleOf(c);
                  return (
                    <TableRow key={`${c.model}-${c.role}`}>
                      <TableCell sx={textCellSx(ui)}>
                        {c.label || c.model}
                        {c.error && (
                          <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>{plainError(c.error)}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={textCellSx(ui)}>
                        <Chip size="small" label={role.label} color={role.color} variant={role.label === "Chosen" ? "filled" : "outlined"} sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }} align="right">{pct(c.wape)}</TableCell>
                      <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }} align="right">{pct(c.wape_total)}</TableCell>
                      <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }} align="right">{signedPct(c.bias)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Box>
  );
}
